// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/** Collection holding one document per controlled-testing case. */
export const CONTROLLED_TESTING_CASES_COLLECTION = 'grc_controlled_testing_cases';
/**
 * Pack vocabulary the case reads from the installed deadline rows. A pack
 * declares the kind of each row in `sla_type`; the platform selects on it, so
 * no row is identified by its id in code.
 */
export const CONTROLLED_TESTING_SLA_TYPES = {
    /** The framework's own effective-date row. The date it states is the cutoff. */
    effectiveDate: 'decree_effective_date',
    /**
     * The row a procedure links to when its transitional rule states no numeric
     * clock. Its presence on a procedure is what makes that procedure's cases
     * eligible for the transitional tag.
     */
    transitionalRule: 'transitional_rule_reference',
    /**
     * The period a recorded personal statement stays current for (Slice B, B3).
     * The row's `deadline` and `unit` are the limit; nothing else is read from it.
     */
    evidenceCurrency: 'evidence_currency_limit',
};
/** The only `source` a computed clock carries: a date reckoned from a recorded anchor. */
export const CONTROLLED_TESTING_CLOCK_SOURCE = 'computed_from_recorded_anchor';
/** Error codes this module returns. Each is a stable machine-readable name. */
export const CONTROLLED_TESTING_ERROR_CODES = {
    SOLUTION_COPY_COORDINATION_CHANGED: 'SOLUTION_COPY_COORDINATION_CHANGED',
    SOLUTION_COPY_PROTOCOL_NOT_READY: 'SOLUTION_COPY_PROTOCOL_NOT_READY',
    SOLUTION_COPY_INTEGRITY_CONFLICT: 'SOLUTION_COPY_INTEGRITY_CONFLICT',
    SOLUTION_COPY_FILE_NOT_HELD: 'SOLUTION_COPY_FILE_NOT_HELD',
    SOLUTION_COPY_HOLDER_CAPACITY: 'SOLUTION_COPY_HOLDER_CAPACITY',
    SOLUTION_COPY_SOURCE_UNVERIFIABLE: 'SOLUTION_COPY_SOURCE_UNVERIFIABLE',
    CHILD_PUBLICATION_NOT_FOUND: 'CHILD_PUBLICATION_NOT_FOUND',
    CHILD_PUBLICATION_CAPACITY: 'CHILD_PUBLICATION_CAPACITY',
    CHILD_PUBLICATION_ALREADY_COMMITTED: 'CHILD_PUBLICATION_ALREADY_COMMITTED',
    CHILD_PUBLICATION_REQUIRES_RECOVERY: 'CHILD_PUBLICATION_REQUIRES_RECOVERY',
    CHILD_PUBLICATION_AUDIT_INTEGRITY: 'CHILD_PUBLICATION_AUDIT_INTEGRITY',
    PARENT_COPY_FILE_UNAVAILABLE: 'PARENT_COPY_FILE_UNAVAILABLE',
    STOP_PROCEEDING_UNRESOLVED: 'STOP_PROCEEDING_UNRESOLVED',
    STOP_EVIDENCE_PROTOCOL_NOT_READY: 'STOP_EVIDENCE_PROTOCOL_NOT_READY',
    INVALID_EVIDENCE_REFERENCE: 'INVALID_EVIDENCE_REFERENCE',
    STOP_REQUEST_NOT_ELIGIBLE: 'STOP_REQUEST_NOT_ELIGIBLE',
    STOP_GROUND_NOT_DECLARED: 'STOP_GROUND_NOT_DECLARED',
    AUTHORITY_TERMINATION_ALREADY_RECORDED: 'AUTHORITY_TERMINATION_ALREADY_RECORDED',
    CASE_NOT_FOUND: 'CASE_NOT_FOUND',
    FRAMEWORK_NOT_INSTALLED: 'FRAMEWORK_NOT_INSTALLED',
    PROCEDURE_NOT_FOUND: 'PROCEDURE_NOT_FOUND',
    /** The framework is installed for reference, or its duties are not owed. Nothing runs. */
    FRAMEWORK_NOT_OPERATIONAL: 'FRAMEWORK_NOT_OPERATIONAL',
    /** The installed procedure declares no lifecycle, or one that does not have the declared shape. */
    PROCEDURE_LIFECYCLE_NOT_DECLARED: 'PROCEDURE_LIFECYCLE_NOT_DECLARED',
    APPLICATION_FORM_NOT_IN_PROCEDURE: 'APPLICATION_FORM_NOT_IN_PROCEDURE',
    /** A transition names a state the installed procedure does not declare. Audited. */
    TRANSITION_NOT_DECLARED: 'TRANSITION_NOT_DECLARED',
    TRANSITION_RULES_NOT_DECLARED: 'TRANSITION_RULES_NOT_DECLARED',
    TRANSITION_PREREQUISITE_NOT_VERIFIED: 'TRANSITION_PREREQUISITE_NOT_VERIFIED',
    TRANSITION_FORBIDDEN_FACT_RECORDED: 'TRANSITION_FORBIDDEN_FACT_RECORDED',
    LIFECYCLE_EVENT_NOT_DECLARED: 'LIFECYCLE_EVENT_NOT_DECLARED',
    LIFECYCLE_EVENT_LIMIT_EXCEEDED: 'LIFECYCLE_EVENT_LIMIT_EXCEEDED',
    RECORDING_REGIME_REVIEW_REQUIRED: 'RECORDING_REGIME_REVIEW_REQUIRED',
    STALE_VERSION: 'STALE_VERSION',
    /** The procedure or form may not change once an authority step is recorded. */
    CASE_ANCHOR_LOCKED: 'CASE_ANCHOR_LOCKED',
    APPLICANT_KIND_NOT_DECLARED: 'APPLICANT_KIND_NOT_DECLARED',
    ENTERED_ON_BEFORE_PREVIOUS_ENTRY: 'ENTERED_ON_BEFORE_PREVIOUS_ENTRY',
    ENTERED_ON_IN_FUTURE: 'ENTERED_ON_IN_FUTURE',
    CHANNEL_REFUSED: 'CHANNEL_REFUSED',
    /**
     * A child case may open only on a parent whose grant is recorded by an installed
     * declaration (E1). `details.reason` says which half is missing: `grant_not_declared`
     * (no installed state of the parent's procedure is declared a grant) or
     * `grant_not_recorded` (the parent has not reached a declared grant).
     */
    PARENT_GRANT_NOT_RECORDED: 'PARENT_GRANT_NOT_RECORDED',
    /** No installed procedure of the framework declares the requested kind of child case. */
    CHILD_KIND_NOT_DECLARED: 'CHILD_KIND_NOT_DECLARED',
    /** More than one installed procedure declares the same kind; the platform does not choose. */
    CHILD_KIND_AMBIGUOUS: 'CHILD_KIND_AMBIGUOUS',
    /** A filing is recorded only against an approval that exists, is approved and is for this child (E4). */
    FILING_APPROVAL_NOT_APPROVED: 'FILING_APPROVAL_NOT_APPROVED',
    /** A child's filing is recorded once. A second filing is refused, never overwritten. */
    FILING_ALREADY_RECORDED: 'FILING_ALREADY_RECORDED',
    APPLICATION_FORMS_NOT_DECLARED: 'APPLICATION_FORMS_NOT_DECLARED',
    FILING_REQUIREMENTS_NOT_DECLARED: 'FILING_REQUIREMENTS_NOT_DECLARED',
    FILING_DATE_IN_FUTURE: 'CONTROLLED_TESTING_FILING_DATE_IN_FUTURE',
    FILED_CUSTOMER_GROUP_REQUIRED: 'CONTROLLED_TESTING_FILED_CUSTOMER_GROUP_REQUIRED',
    RESULTS_REPORT_REQUIRED: 'CONTROLLED_TESTING_RESULTS_REPORT_REQUIRED',
    RESULTS_REPORT_NOT_HELD: 'CONTROLLED_TESTING_RESULTS_REPORT_NOT_HELD',
    /** A stop is filed once. A second stop is refused, never overwritten (F2). */
    STOP_ALREADY_FILED: 'STOP_ALREADY_FILED',
    /** A revocation is recorded only with the authority’s decision reference (F3). */
    DECISION_REFERENCE_REQUIRED: 'DECISION_REFERENCE_REQUIRED',
    /** A revocation needs a filed stop to revoke (F3). */
    STOP_NOT_FILED: 'STOP_NOT_FILED',
    /** The authority's decision is recorded once (F3). */
    REVOCATION_ALREADY_RECORDED: 'REVOCATION_ALREADY_RECORDED',
    /** A file named by a stop, completion or closure record is not held under this case (F2, F4, F5). */
    OUTCOME_EVIDENCE_NOT_OWNED: 'OUTCOME_EVIDENCE_NOT_OWNED',
    /** Completion is filed once (F4). */
    COMPLETION_ALREADY_FILED: 'COMPLETION_ALREADY_FILED',
    /** The certificate is recorded only against a filed completion (F4). */
    COMPLETION_NOT_FILED: 'COMPLETION_NOT_FILED',
    /** The authority's copy of the certificate is recorded once (F4). */
    CERTIFICATE_ALREADY_RECORDED: 'CERTIFICATE_ALREADY_RECORDED',
    /** Closure is recorded only once the authority's stop decision is recorded (F5). */
    CLOSURE_NOT_AVAILABLE: 'CLOSURE_NOT_AVAILABLE',
    /** The liquidation record is recorded once (F5). */
    LIQUIDATION_ALREADY_RECORDED: 'LIQUIDATION_ALREADY_RECORDED',
    /** A stop decision date is a date the State Bank decided on, so it cannot be after today. */
    STOP_DECISION_IN_FUTURE: 'STOP_DECISION_IN_FUTURE',
    /** A settlement completion date cannot be before its recorded stop-decision anchor. Without that anchor, the reference is pending. */
    SETTLEMENT_BEFORE_STOP: 'SETTLEMENT_BEFORE_STOP',
    /** Each anchor date is entered once. A second entry is refused, never overwritten. */
    POST_STOP_ANCHOR_ALREADY_RECORDED: 'POST_STOP_ANCHOR_ALREADY_RECORDED',
    ANCHOR_NOT_RECORDED: 'ANCHOR_NOT_RECORDED',
    ANCHOR_CORRECTION_NO_CHANGE: 'ANCHOR_CORRECTION_NO_CHANGE',
    /**
     * The authority's stop-and-revoke decision is recorded, so the case is ended: no completion,
     * certificate, authority step or child case is recorded on it. Audited (Finding 12).
     */
    CASE_STOPPED_AND_REVOKED: 'CASE_STOPPED_AND_REVOKED',
    /** The authority's certificate of completion is recorded, so no stop can be filed. Audited (Finding 12). */
    CASE_COMPLETION_CERTIFIED: 'CASE_COMPLETION_CERTIFIED',
    /** No installed row of the case's framework declares the direction kind (Finding 11). */
    DIRECTION_KIND_NOT_DECLARED: 'DIRECTION_KIND_NOT_DECLARED',
    /**
     * Sprint 62.51.0 (CL-3) — the case's installed procedure declares
     * `outcome_filings` and does not list this filing, so it is not recorded on the
     * case. `details.filing` names it (`stop` or `completion`); `details.offered`
     * lists the filings the procedure offers; `details.recorded_on` lists the
     * register surfaces of the procedure's templates, where the outcome is recorded.
     */
    OUTCOME_FILING_NOT_DECLARED: 'OUTCOME_FILING_NOT_DECLARED',
    /**
     * Sprint 62.51.0 Remediation X (G1-7) — 409: a personnel, solution or
     * registry record, or a child case, written on a case whose
     * `derived.declared_sections` says the section is not drawn (no installed
     * declaration in the case's amendment family and nothing held there).
     * `details.section` names the section; the server refuses what the page
     * would not show.
     */
    CASE_SECTION_NOT_DECLARED: 'CASE_SECTION_NOT_DECLARED',
};
// ── Slice E: child cases (adjustment and extension, 63.24.0 E1–E4) ──
/** The kinds of child case a case may open. The pack declares which procedure is which (see below). */
export const CONTROLLED_TESTING_CHILD_KINDS = ['adjustment', 'extension'];
/**
 * The attributes the platform reads from an installed pack. These are vocabulary only: no
 * installed pack declares either attribute yet (owner questions OQ-E-1 and OQ-E-2 in the
 * Slice E work file). Until one does, the platform refuses every child case and says why;
 * it never reads a grant from a state's name (A8), nor a child kind from a procedure's id.
 *
 * - `is_grant` on a lifecycle state: the authority's decision that grants the requested act.
 * - `child_case_kind` on a procedure: the kind of child case the procedure is.
 */
export const CONTROLLED_TESTING_DECLARATION_KEYS = {
    grantState: 'is_grant',
    childCaseKind: 'child_case_kind',
};
/** The `entity_type` an internal sign-off for a child case is raised under (E4). */
export const CONTROLLED_TESTING_CHILD_APPROVAL_ENTITY_TYPE = 'controlled_testing_child_case';
/**
 * The format status of an extension's results report (E3). The Decree 94 Phụ lục V format is
 * in the committed transcript but has not been read into the framework record, so the only
 * status the platform states is `provisional`. No format is shown as verified.
 */
export const CONTROLLED_TESTING_RESULTS_REPORT_FORMAT_STATUS = 'provisional';
/** The basis the results report's marker states. Pack vocabulary is not used: it is the platform's own statement. */
export const CONTROLLED_TESTING_RESULTS_REPORT_BASIS = 'appendix_not_in_corpus';
// ── Slice F: stop, completion and closure (63.24.0, F1–F6) ──
/**
 * The stop's state, derived on read from what is recorded. There is no suspended state, and no
 * state of a stop is one the platform decides (F1): `filed` waits for the authority's decision,
 * `decision_recorded` holds the authority's decision as the authority gave it (F3).
 */
export const CONTROLLED_TESTING_STOP_STATES = ['not_filed', 'filed', 'decision_recorded'];
/** The completion's state, derived on read (F4). */
export const CONTROLLED_TESTING_COMPLETION_STATES = [
    'not_filed',
    'filed',
    'certificate_recorded',
];
/** The three files a stop is filed with (F2). Each is named by its role, and each must be held under this case. */
export const CONTROLLED_TESTING_STOP_ROLES = [
    'application',
    'results_report',
    'stop_plan',
];
/** The two files a completion is filed with (F4). */
export const CONTROLLED_TESTING_COMPLETION_ROLES = ['evaluation_report', 'results_report'];
/**
 * The only copy source a certificate may be recorded from (F4). The platform never issues a
 * certificate: it holds the authority's own copy and says where the copy came from.
 */
export const CONTROLLED_TESTING_CERTIFICATE_COPY_SOURCE = 'authority_copy';
/**
 * The two post-stop duties the platform reads (F5). Each is matched to an installed deadline row
 * only through the row's declared `post_stop_duty` value; no row is matched by its id or its text.
 */
export const CONTROLLED_TESTING_POST_STOP_DUTY_KINDS = [
    'entitlements_settled',
    'settlement_report',
];
/**
 * The two dates a post-stop duty runs from (owner ruling 2026-10-11). A person enters each on the
 * closure record with the evidence it rests on. The platform never derives either date from
 * another date, and a duty whose anchor is not entered shows no date.
 * - `stop_decision`: the State Bank's stop decision date.
 * - `settlement_completion`: the date the settlement of the customer's rights and obligations is complete.
 */
export const CONTROLLED_TESTING_POST_STOP_ANCHOR_KINDS = [
    'stop_decision',
    'settlement_completion',
];
/**
 * The attributes the platform reads from an installed pack, for Slice F. No installed pack
 * declares either yet (owner questions OQ-F-1 and OQ-F-4 in the Slice F work file). Until one
 * does, the page says "not declared by an installed framework" and computes nothing from them.
 *
 * - `stop_grounds` on a procedure: the grounds the stop may rest on, each with its label and citation.
 * - `post_stop_duty` on a deadline row: which post-stop duty the row is, from `CONTROLLED_TESTING_POST_STOP_DUTY_KINDS`.
 * - `post_stop_scope` on the same row: the solution points the duty applies to (pack 1.0.9). Read through
 *   `InstalledPackPostStopScope`; a row with no scope reads as before.
 */
export const CONTROLLED_TESTING_OUTCOME_DECLARATION_KEYS = {
    stopGrounds: 'stop_grounds',
    postStopDuty: 'post_stop_duty',
    postStopScope: 'post_stop_scope',
    /**
     * Sprint 62.51.0 (CL-3) — on a procedure: the organisation outcome filings the
     * procedure offers, a list drawn from {@link CONTROLLED_TESTING_OUTCOME_FILINGS}.
     * ABSENT means both, so every installed pack keeps its behaviour. A procedure
     * whose outcome is recorded elsewhere (a register that holds the authority's
     * own acts) declares the filings it offers — none, or one — and the case core
     * refuses the others with 409 `OUTCOME_FILING_NOT_DECLARED`.
     */
    outcomeFilings: 'outcome_filings',
    postStopAnchor: 'post_stop_anchor',
};
/**
 * Sprint 62.51.0 (CL-3) — the two organisation outcome filings the case core
 * offers: the stop the organisation files (with the authority's revocation and
 * the closure after it) and the completion it files (with the authority's
 * certificate after it). Platform vocabulary: the members a procedure's
 * `outcome_filings` may name.
 */
export const CONTROLLED_TESTING_OUTCOME_FILINGS = ['stop', 'completion'];
/**
 * Sprint 62.51.0 Remediation W (U1) — the case-page sections whose presence
 * rests on an installed declaration. The case view's
 * `derived.declared_sections` answers each with a boolean, read now from the
 * installed records (never stored):
 *
 * - `personnel`, `solution`, `registry`: an installed template declaring that
 *   sub-register's surface (`controlled_testing_personnel`, `…_solution`,
 *   `…_registry`) belongs to the case's framework, or to a framework in its
 *   amendment family (the framework the case's framework amends, or one that
 *   amends it — `amends_framework_code`);
 * - `child_cases`: a procedure of that family declares `child_case_kind`;
 * - `stop`, `completion`: the case's procedure offers that outcome filing
 *   (`outcome_filings`; an absent key reads both — the pre-62.51.0 default);
 * - `closure` (closure notices, liquidation, anchors, post-stop duties):
 *   follows `stop`, since every closure record follows a stop;
 * - `directions`: an installed direction row (`request_types`) of that family
 *   declares a kind a case may record.
 *
 * An absent declaration reads `false`; the one default kept is the absent
 * `outcome_filings` key, so a procedure authored before it is drawn as before.
 *
 * Remediation X (G1-1): a framework record or procedure that can no longer be
 * read (the pack uninstalled, the procedure renamed in a new version) is
 * UNDECLARED, not declared empty — every key reads `true`, as before this
 * sprint, so no case loses a section, a filing or its evidence path. And a
 * section that HOLDS records for the case (sub-register rows, child cases, a
 * stop, a completion, a closure, direction links) always reads `true`.
 */
export const CONTROLLED_TESTING_CASE_SECTIONS = [
    'personnel',
    'solution',
    'registry',
    'child_cases',
    'stop',
    'completion',
    'closure',
    'directions',
];
//# sourceMappingURL=controlled-testing.js.map